import { useCallback, useEffect, useState } from "react";
import { Trash2, TriangleAlert } from "lucide-react";
import Input from "@/components/common/Input";
import ToggleInput from "@/components/common/ToggleInput";
import fontCategoryApi from "@/api/font-category";
import { FontCategory } from "@/interfaces/font.interface";
import { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";

interface Props {
  data: FontCategory;
  modifiable?: boolean;
  selectedCategory?: FontCategory;
  onSelect?: (category?: FontCategory) => void;
  onDelete?: (category: FontCategory) => void;
}

export default function CategoryNode(props: Props) {
  const { data, modifiable, selectedCategory, onSelect, onDelete } = props;
  const [children, setChildren] = useState<FontCategory[]>();
  const [expanded, setExpanded] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [creating, setCreating] = useState(false);

  const getChildren = useCallback(() => {
    fontCategoryApi
      .getMany({ sort: "-createdAt", parentId: data._id })
      .then((data) => {
        const categories = data.data;
        setChildren(categories);
      })
      .catch(() => {});
  }, [data]);

  useEffect(() => {
    data.actions = {
      create: () => {
        setExpanded(true);
        setCreating(true);
      },
      read: getChildren,
    };
  }, [data, getChildren]);

  const onRename = (newName: string) => {
    setRenaming(false);
    newName = newName.trim();
    if (!newName || newName === data.name) return;
    data.name = newName;
    fontCategoryApi
      .updateOneById(data._id, { name: newName })
      .then(() => {})
      .catch(() => {});
  };

  const onExpand = () => {
    if (children) return;
    getChildren();
  };

  const onCreate = (name: string) => {
    setCreating(false);
    if (!name) return;
    fontCategoryApi
      .createOne({
        parentId: data._id,
        name,
      })
      .then((data) => setChildren((pre) => [data.data, ...(pre || [])]))
      .catch(() => {});
  };

  const onDeleteChild = (category: FontCategory) => {
    fontCategoryApi
      .deleteOneById(category._id)
      .then((data) => {
        const deletedCategory = data.data;
        setChildren((state) =>
          state?.filter((c) => c._id !== deletedCategory._id),
        );
        if (
          selectedCategory?._id === deletedCategory._id ||
          selectedCategory?.ancestorIds.includes(deletedCategory._id)
        )
          onSelect?.(undefined);
      })
      .catch(() => {});
  };

  const onConfirmDelete = (event: any) => {
    confirmPopup({
      target: event.currentTarget,
      message: "This action cannot be undone. Confirm?",
      icon: <TriangleAlert color="red" />,
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => onDelete?.(data),
      reject: () => {},
    });
  };

  return (
    <div className={"w-full"}>
      <div
        className={`group hover:bg-primary-300 relative flex items-center gap-1 hover:text-white`}
      >
        <div
          className={`flex max-h-8 min-h-8 w-full cursor-pointer items-center gap-1 ${selectedCategory?._id === data._id ? "bg-primary-300 text-white" : ""}`}
          style={{
            paddingLeft: data.ancestorIds.length * 16 + 8,
          }}
          onClick={() => {
            setExpanded(!expanded);
            onSelect?.(data);
            onExpand();
          }}
        >
          {expanded ? (
            <i className="pi pi-chevron-down" style={{ fontSize: 14 }} />
          ) : (
            <i className="pi pi-chevron-right" style={{ fontSize: 14 }} />
          )}
          <ToggleInput
            className={`text-md flex-1`}
            inputClassName="truncate"
            inputStyle={{ height: 30 }}
            defaultValue={data.name}
            onBeforeChange={() => setRenaming(true)}
            onAfterChange={onRename}
          />
        </div>
        {!renaming && (
          <div className="absolute right-1 hidden rounded group-hover:block">
            <div className="flex h-full items-center gap-1">
              <Button
                severity="danger"
                type="button"
                outlined
                tooltip="Delete category"
                tooltipOptions={{ position: "top" }}
                style={{
                  padding: 0,
                  width: 20,
                  height: 20,
                  backgroundColor: "white",
                }}
                icon={<Trash2 size={14} />}
                onClick={onConfirmDelete}
              />
            </div>
          </div>
        )}
      </div>

      <div className={`${expanded ? "block" : "hidden"}`}>
        {creating && (
          <div
            className="flex items-center gap-1"
            style={{
              paddingLeft: (data.ancestorIds.length + 1) * 16 + 8,
            }}
          >
            <i className="pi pi-chevron-right" style={{ fontSize: 12 }}></i>
            <Input
              type="text"
              style={{ border: "none" }}
              autoFocus
              onBlur={(event) => onCreate(event.target.value)}
              onKeyDown={(event) => {
                if (event.code === "Enter") {
                  event.currentTarget.blur();
                }
              }}
            />
          </div>
        )}
        {children?.map((c) => (
          <CategoryNode
            key={c._id}
            data={c}
            modifiable={modifiable}
            selectedCategory={selectedCategory}
            onSelect={onSelect}
            onDelete={onDeleteChild}
          />
        ))}
      </div>
    </div>
  );
}
